import {
  Box,
  Chip,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import color from "../../Constants/colors";
import { useNavigate } from "react-router-dom";
import {
  useMcpConnections,
  useDeleteMcpConnection,
  useTestMcpConnection,
  useRefreshMcpTools,
} from "../../Query/useComoAgent";
import { useState } from "react";
import ConfirmationDialog from "../Common/ConfirmationDialog";
import Loading from "../Common/Loading";
import { useWorkspaceColors } from "../../Contexts/WorkspaceColorContext";
import { enqueueSnackbar } from "notistack";
import { useTranslation } from "react-i18next";

const McpConnectionsList = () => {
  const navigate = useNavigate();
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const { mutate: deleteConnection } = useDeleteMcpConnection();
  const testMutation = useTestMcpConnection();
  const refreshMutation = useRefreshMcpTools();
  const { colors: workspace } = useWorkspaceColors();
  const { t } = useTranslation("mcpConnectionForm");

  const { data, isLoading, isError } = useMcpConnections();

  const handleConfirmDelete = (confirmed: boolean) => {
    setConfirmationDialogOpen(false);
    if (confirmed && selectedId) {
      deleteConnection(selectedId);
    }
  };

  const handleTest = (id: number) => {
    testMutation.mutate(id, {
      onSuccess: (result) => {
        if (result.success) {
          enqueueSnackbar(
            t("connectionSuccessful", {
              count: result.tools_count,
            }),
            { variant: "success" },
          );
        } else {
          enqueueSnackbar(
            t("connectionFailed", {
              error: result.error,
            }),
            {
              variant: "error",
            },
          );
        }
      },
    });
  };

  if (isLoading) {
    return (
      <Loading
        backgroundColor={color.gray100}
        color1={workspace.accent}
        color2={workspace.primary_dark}
      />
    );
  }

  if (isError) {
    return (
      <Box sx={{ p: 4, color: color.red700 }}>
        Error: {t("failedToFetchConnections")}
      </Box>
    );
  }

  return (
    <Box
      sx={{ minHeight: "calc(100vh - 64px)", backgroundColor: color.gray50 }}
    >
      <Box sx={{ maxWidth: "1024px", mx: "auto", px: { xs: 2, sm: 3 }, py: 4 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            borderRadius: 2,
            border: `1px solid ${color.gray200}`,
            borderLeft: `4px solid ${workspace.primary_dark}`,
            background: color.white,
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
            spacing={2}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <HubOutlinedIcon
                sx={{ fontSize: 32, color: workspace.primary_dark }}
              />
              <Box>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 600, color: workspace.primary_dark }}
                >
                  {t("mcpConnections")}
                </Typography>
                <Typography variant="caption" sx={{ color: color.gray500 }}>
                  {data?.length ?? 0}
                </Typography>
              </Box>
            </Stack>

            <IconButton
              onClick={() => navigate("/mcp_connections/create")}
              sx={{
                bgcolor: workspace.accent,
                color: workspace.primary_dark,
                borderRadius: 1.5,
                px: 1.5,
                py: 1,
                gap: 0.75,
                "&:hover": { bgcolor: workspace.accent, opacity: 0.9 },
              }}
            >
              <AddCircleOutlineIcon sx={{ fontSize: 20 }} />
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, fontSize: "0.85rem" }}
              >
                {t("createNew")}
              </Typography>
            </IconButton>
          </Stack>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 },
            borderRadius: 2,
            border: `1px solid ${color.gray200}`,
            borderLeft: `4px solid ${workspace.primary_dark}`,
            background: color.white,
          }}
        >
          <TableContainer
            sx={{
              border: `1px solid ${color.gray200}`,
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: color.gray50 }}>
                  <TableCell
                    sx={{
                      color: color.gray600,
                      fontWeight: 600,
                      fontSize: "0.8rem",
                      textTransform: "uppercase",
                    }}
                  >
                    {t("name")}
                  </TableCell>
                  <TableCell
                    sx={{
                      color: color.gray600,
                      fontWeight: 600,
                      fontSize: "0.8rem",
                      textTransform: "uppercase",
                    }}
                  >
                    {t("url")}
                  </TableCell>
                  <TableCell
                    sx={{
                      color: color.gray600,
                      fontWeight: 600,
                      fontSize: "0.8rem",
                      textTransform: "uppercase",
                    }}
                  >
                    {t("tools")}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      color: color.gray600,
                      fontWeight: 600,
                      fontSize: "0.8rem",
                      textTransform: "uppercase",
                    }}
                  >
                    {t("actions")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(data ?? []).map((conn) => (
                  <TableRow
                    key={conn.id}
                    sx={{ "&:hover": { bgcolor: color.gray50 } }}
                  >
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, color: color.primary_dark }}
                      >
                        {conn.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ color: color.gray600, fontSize: "0.8rem" }}
                      >
                        {conn.url}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {(conn.cached_tools || []).length > 0 ? (
                          <Chip
                            label={t("toolsCount", {
                              count: conn.cached_tools.length,
                            })}
                            size="small"
                            sx={{
                              bgcolor: color.green50,
                              color: color.green700,
                              fontSize: "0.75rem",
                              height: 24,
                            }}
                          />
                        ) : (
                          <Typography
                            variant="caption"
                            sx={{ color: color.gray400 }}
                          >
                            {t("noToolsDiscovered")}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          gap: 0.5,
                        }}
                      >
                        <Tooltip title={t("testConnection")}>
                          <IconButton
                            size="small"
                            onClick={() => handleTest(conn.id)}
                            sx={{ color: color.green700 }}
                          >
                            <PlayArrowIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t("refreshTools")}>
                          <IconButton
                            size="small"
                            onClick={() => refreshMutation.mutate(conn.id)}
                            sx={{ color: color.secondary }}
                          >
                            <RefreshIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t("edit")}>
                          <IconButton
                            size="small"
                            onClick={() =>
                              navigate(`/mcp_connections/edit/${conn.id}`)
                            }
                            sx={{ color: color.secondary }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t("delete")}>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSelectedId(conn.id);
                              setConfirmationDialogOpen(true);
                            }}
                            sx={{
                              color: color.gray400,
                              "&:hover": { color: color.red },
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
                {(data ?? []).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ textAlign: "center", py: 6 }}>
                      <Typography sx={{ color: color.gray400 }}>
                        {t("noMcpConnectionsYet")}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>

      <ConfirmationDialog
        open={confirmationDialogOpen}
        onClose={handleConfirmDelete}
        title={t("deleteMcpConnection")}
        description={t("deleteMcpConnectionDescription")}
        content={t("deleteMcpConnectionContent")}
        actions={[
          { label: t("no"), onClick: () => handleConfirmDelete(false) },
          { label: t("yes"), onClick: () => handleConfirmDelete(true) },
        ]}
      />
    </Box>
  );
};

export default McpConnectionsList;
